import { TotalActiveVehiculeCheck } from '@/api/entities/vehicule-check';
import {
  createNewVehiculeCheck,
  deleteManyVehiculeChecks,
  deleteVehiculeCheck,
  getAllVehiculeChecks,
  getAllVehiculeChecksToModerate,
  getAllVehiculeChecksToValidate,
  getCreatedVehiculeChecksPerMonth,
  getExistingVehiculeCheck,
  getTotalActiveVehiculeChecks,
  getVehiculeCheckById,
  updateVehiculeCheck,
  validateModeration,
} from '@/api/vehicule-check/vehicule-check';
import { VehiculeCheckFilterType } from '@/entities/vehicle';
import { TVehiculeCheckSchema } from '@/schemas/vehicule-check/vehicule-check.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { NOTIFY } from '@/utils/constant.utils';
import { notify } from '@/utils/toast.utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeleteManyVehiculeCheckResultsByVehiculeCheckId } from './vehicule-check-result.hooks';

export const useCreateVehiculeCheck = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TVehiculeCheckSchema) => createNewVehiculeCheck(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeChecks'],
      });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehiculeCheck'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehicule'] });
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });

      notify(t('inspectionCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetExistingVehiculeCheck = (query?: TVehiculeCheckSchema) => {
  return useQuery({
    queryKey: ['getExistingVehiculeCheck', query],
    queryFn: () => getExistingVehiculeCheck(query),
  });
};

export const useGetAllVehiculeChecks = (
  filter: VehiculeCheckFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  return useQuery({
    queryKey: ['getAllVehiculeChecks', filter, page, sortBy, sortDirection],
    queryFn: () =>
      getAllVehiculeChecks(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveVehiculeChecks = (
  query?: TotalActiveVehiculeCheck
) => {
  return useQuery({
    queryKey: ['getTotalActiveVehiculeChecks', query],
    queryFn: () => getTotalActiveVehiculeChecks(query),
  });
};

export const useGetVehiculeCheckById = (id: string) => {
  return useQuery({
    queryKey: ['getVehiculeCheckById', id],
    queryFn: () => getVehiculeCheckById(id),
    enabled: !!id,
  });
};

export const useGetCreatedVehiculeChecksPerMonth = (userId?: string) => {
  return useQuery({
    queryKey: ['getCreatedVehiculeChecksPerMonth', userId],
    queryFn: () => getCreatedVehiculeChecksPerMonth(userId),
  });
};

export const useGetAllVehiculeChecksToModerate = (userId?: string) => {
  return useQuery({
    queryKey: ['getAllVehiculeChecksToModerate', userId],
    queryFn: () => getAllVehiculeChecksToModerate(userId),
  });
};

export const useGetAllVehiculeChecksToValidate = (userId?: string) => {
  return useQuery({
    queryKey: ['getAllVehiculeChecksToValidate', userId],
    queryFn: () => getAllVehiculeChecksToValidate(userId),
  });
};

export const useUpdateVehiculeCheck = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehiculeCheck: TVehiculeCheckSchema) =>
      updateVehiculeCheck(vehiculeCheck),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeCheckById', id] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehiculeCheck'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehicule'] });
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });

      notify(t('inspectionUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      handleError?.(err);
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicule check:', err.error);
    },
  });
};

export const useValidateModeration = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (idVehicleCheck: string) => validateModeration(idVehicleCheck),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeCheckById'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehiculeCheck'] });
      queryClient.invalidateQueries({
        queryKey: ['getResultByVehiculeCheckId'],
      });
      notify(t('moderationValidation'), NOTIFY.SUCCESS);
      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      handleError?.(err);
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when validating moderation:', err.error);
    },
  });
};

export const useDeleteVehiculeCheck = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteVehiculeCheck(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeChecks'],
      });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehiculeCheck'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehicule'] });

      notify(t('inspectionDeletedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.({ id });
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useDeleteManyVehiculeChecks = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  const { mutate: deleteManyVehiculeCheckResults } =
    useDeleteManyVehiculeCheckResultsByVehiculeCheckId();

  return useMutation({
    mutationFn: (ids: string[]) => deleteManyVehiculeChecks(ids),
    onSuccess: (_, ids) => {
      for (const id of ids) {
        deleteManyVehiculeCheckResults(id);
      }

      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeChecks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getExistingVehiculeCheck'],
      });
      queryClient.invalidateQueries({ queryKey: ['getExistingVehicule'] });

      notify(
        ids.length > 1
          ? t('inspectionsDeletedSuccessfully')
          : t('inspectionDeletedSuccessfully'),
        NOTIFY.SUCCESS
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};
